import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { 
  EmailInvitation,
  EmailInvitationRequest,
  BulkEmailInvitationRequest,
  EmailInvitationResponse,
  BulkEmailInvitationResponse,
  EmailInvitationListResponse
} from '@poker-tracker/shared';
import { apiClient } from '../apiClient';

// Query key factory
const emailInvitationKeys = {
  all: ['emailInvitations'] as const,
  lists: () => [...emailInvitationKeys.all, 'list'] as const,
  list: (gameId: string) => [...emailInvitationKeys.lists(), gameId] as const,
  details: () => [...emailInvitationKeys.all, 'detail'] as const,
  detail: (invitationId: string) => [...emailInvitationKeys.details(), invitationId] as const,
};

/**
 * Hook to get email invitations for a game
 */
export function useEmailInvitations(gameId: string) {
  return useQuery({
    queryKey: emailInvitationKeys.list(gameId),
    queryFn: async (): Promise<EmailInvitation[]> => {
      const response = await apiClient.get<EmailInvitationListResponse>(`/v1/games/${gameId}/invitations`);
      // apiClient returns data directly, not wrapped in response.data
      return response.invitations;
    },
    enabled: !!gameId,
    staleTime: 30000, // 30 seconds
    refetchInterval: 60000, // Refetch every minute to get status updates
  });
}

/**
 * Hook to send a single email invitation
 */
export function useSendEmailInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: EmailInvitationRequest): Promise<EmailInvitationResponse> => {
      const response = await apiClient.post<EmailInvitationResponse>(
        `/v1/games/${request.gameId}/invitations`,
        request
      );
      // apiClient returns data directly, not wrapped in response.data
      return response;
    },
    onSuccess: (data, variables) => {
      // Invalidate and refetch invitations for this game
      queryClient.invalidateQueries({
        queryKey: emailInvitationKeys.list(variables.gameId)
      });
      
      // Also invalidate players list as invitation might affect player count
      queryClient.invalidateQueries({
        queryKey: ['players', variables.gameId]
      });
    },
    onError: (error) => {
      console.error('Error sending email invitation:', error);
    }
  });
}

/**
 * Hook to send bulk email invitations
 */
export function useSendBulkEmailInvitations() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: BulkEmailInvitationRequest): Promise<BulkEmailInvitationResponse> => {
      const response = await apiClient.post<BulkEmailInvitationResponse>(
        `/v1/games/${request.gameId}/invitations/bulk`,
        request
      );
      // apiClient returns data directly, not wrapped in response.data
      return response;
    },
    onSuccess: (data, variables) => {
      // Invalidate and refetch invitations for this game
      queryClient.invalidateQueries({
        queryKey: emailInvitationKeys.list(variables.gameId)
      });
      
      // Also invalidate players list as invitations might affect player count
      queryClient.invalidateQueries({
        queryKey: ['players', variables.gameId]
      });
    },
    onError: (error) => {
      console.error('Error sending bulk email invitations:', error);
    }
  });
}

/**
 * Hook to track email events (for analytics)
 */
export function useTrackEmailEvent() {
  return useMutation({
    mutationFn: async ({ 
      invitationId, 
      eventType, 
      metadata 
    }: { 
      invitationId: string; 
      eventType: string; 
      metadata?: any 
    }) => {
      const response = await apiClient.post(`/v1/invitations/${invitationId}/track`, {
        eventType,
        metadata
      });
      // apiClient returns data directly, not wrapped in response.data
      return response;
    },
    onError: (error) => {
      // Don't show errors to user for tracking events
      console.warn('Error tracking email event:', error);
    }
  });
}

/**
 * Hook to get RSVP page data
 */
export function useRSVPData(invitationId: string) {
  const result = useQuery({
    queryKey: ['rsvp', invitationId],
    queryFn: async () => {
      const response = await apiClient.get(`/v1/rsvp/${invitationId}`);
      
      // The apiClient.get might be returning the data directly, not wrapped in a response object
      const actualData = response.data || response;
      return actualData;
    },
    enabled: !!invitationId,
    retry: false, // Don't retry if invitation is not found or expired
  });

  return result;
}

/**
 * Hook to submit RSVP response
 */
export function useSubmitRSVP() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      invitationId, 
      rsvpStatus, 
      playerName 
    }: { 
      invitationId: string; 
      rsvpStatus: string; 
      playerName?: string 
    }) => {
      const response = await apiClient.post(`/v1/rsvp/${invitationId}`, {
        rsvpStatus,
        playerName
      });
      // apiClient returns data directly, not wrapped in response.data
      return response;
    },
    onSuccess: (data, variables) => {
      // Invalidate RSVP data
      queryClient.invalidateQueries({
        queryKey: ['rsvp', variables.invitationId]
      });
    },
    onError: (error) => {
      console.error('Error submitting RSVP:', error);
    }
  });
}

/**
 * Hook to get email invitation statistics for a game
 */
export function useEmailInvitationStats(gameId: string) {
  return useQuery({
    queryKey: ['emailInvitationStats', gameId],
    queryFn: async () => {
      const invitations = await apiClient.get<EmailInvitationListResponse>(`/v1/games/${gameId}/invitations`);
      
      // Calculate stats from invitations data
      // apiClient returns data directly, not wrapped in response.data
      const data = invitations.invitations;
      const totalSent = data.length;
      const totalOpened = data.filter(i => ['opened', 'responded'].includes(i.status)).length;
      // Count as responded if they have an rsvpStatus that's not pending
      const totalResponded = data.filter(i => i.rsvpStatus && i.rsvpStatus !== 'pending').length;
      const totalAccepted = data.filter(i => i.rsvpStatus === 'accepted').length;
      const totalDeclined = data.filter(i => i.rsvpStatus === 'declined').length;
      
      return {
        gameId,
        totalSent,
        totalOpened,
        totalResponded,
        totalAccepted,
        totalDeclined,
        openRate: totalSent > 0 ? Math.round((totalOpened / totalSent) * 100) : 0,
        responseRate: totalSent > 0 ? Math.round((totalResponded / totalSent) * 100) : 0,
        acceptanceRate: totalResponded > 0 ? Math.round((totalAccepted / totalResponded) * 100) : 0
      };
    },
    enabled: !!gameId,
    staleTime: 60000, // 1 minute
  });
}

/**
 * Hook to resend an email invitation
 */
export function useResendEmailInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      gameId, 
      recipientEmail 
    }: { 
      gameId: string; 
      recipientEmail: string 
    }) => {
      // Create a new invitation request for the same recipient
      const request: EmailInvitationRequest = {
        gameId,
        recipientEmail,
        templateType: 'game_reminder'
      };
      
      const response = await apiClient.post<EmailInvitationResponse>(
        `/v1/games/${gameId}/invitations`,
        request
      );
      // apiClient returns data directly, not wrapped in response.data
      return response;
    },
    onSuccess: (data, variables) => {
      // Invalidate and refetch invitations for this game
      queryClient.invalidateQueries({
        queryKey: emailInvitationKeys.list(variables.gameId)
      });
    },
    onError: (error) => {
      console.error('Error resending email invitation:', error);
    }
  });
}

/**
 * Custom hook to manage email invitation form state
 */
export function useEmailInvitationForm(gameId: string) {
  const sendBulkInvitations = useSendBulkEmailInvitations();

  const handleSendBulkInvitations = async (request: BulkEmailInvitationRequest) => {
    return sendBulkInvitations.mutateAsync(request);
  };

  return {
    sendBulkInvitations: handleSendBulkInvitations,
    isLoading: sendBulkInvitations.isPending,
    error: sendBulkInvitations.error
  };
}
